import { lazy, ComponentType, LazyExoticComponent } from 'react';
import { safeSessionStorage } from './storage';

/** sessionStorage key holding the time of the last automatic reload after a failed chunk. */
export const CHUNK_RELOAD_KEY = 'chunkReloadAt';
/** A second failed chunk within this time after the automatic reload shows the fallback instead. */
export const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;
const RETRY_DELAY_MS = 500;

/**
 * Whether an error is a failed fetch of a code-split chunk (a network hiccup,
 * or a chunk name that no longer exists after a deploy): webpack's
 * `ChunkLoadError` ("Loading chunk x failed", "Loading CSS chunk x failed")
 * and the browsers' messages for a failed `import()`.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') {
    return true;
  }
  return typeof message === 'string' && (
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message)
    || message.includes('Failed to fetch dynamically imported module')
    || message.includes('error loading dynamically imported module')
    || message.includes('Importing a module script failed')
  );
};

/**
 * Reloads the page once after a failed chunk. Returns false (no reload) when it
 * already did so within CHUNK_RELOAD_WINDOW_MS, or when sessionStorage cannot
 * keep the flag across the reload (blocked storage), so it can never loop.
 */
export const reloadOnceForChunkError = (): boolean => {
  const last = Number(safeSessionStorage.getItem(CHUNK_RELOAD_KEY) || '0');
  const now = Date.now();
  if (last && now - last < CHUNK_RELOAD_WINDOW_MS) {
    return false;
  }
  if (!safeSessionStorage.setItem(CHUNK_RELOAD_KEY, String(now))) {
    return false;
  }
  window.location.reload();
  return true;
};

/**
 * `React.lazy()` that retries a failed chunk import once (webpack requests a
 * failed chunk again on the next `import()`), so a single dropped request does
 * not reach the error boundary.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => factory().catch((error: unknown) => {
    if (!isChunkLoadError(error)) {
      throw error;
    }
    return new Promise<{ default: T }>((resolve, reject) => {
      setTimeout(() => factory().then(resolve, reject), RETRY_DELAY_MS);
    });
  }));
}
